import { IconExpandMore, IconLogout, IconUser } from "@tine/designsystem-icons/sharp";
import { Button } from "@tine/designsystem-ui-react";
import { cn } from "@tine/designsystem-utils";
import { observer } from "mobx-react-lite";
import { type MouseEventHandler, useRef, useState } from "react";
import { Link, useLocation } from "wouter";
import NotificationsBadge from "../../account/notifications/NotificationsBadge";
import { sendInternalLinkClick } from "../../common/tracking";
import useMediaQuery from "../../common/useMediaQuery";
import { mediaQueries } from "../../common/utils";
import { useMenuItems } from "../../common/utils/menuItems";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/common/popover/Popover";
import authStore from "../../stores/auth/authStore";
import loginState from "../../stores/auth/loginState";
import contentStore from "../../stores/cms/contentStore";
import featuresStore from "../../stores/features/featuresStore";
import notificationsStore from "../../stores/notifications/notificationsStore";
import { getNotificationsTrackingText } from "../../stores/notifications/notificationsUtils";

const UserMenu = () => {
   const [_, redirectTo] = useLocation();
   const [show, setShow] = useState(false);
   const menuItems = useMenuItems();
   const buttonRef = useRef<HTMLButtonElement>(null);

   const newPromotionArticlesCount = contentStore.getNewPromotionArticlesCount();
   const notificationsCount = notificationsStore.notifications.data.length;
   const totalNotificationsCount = notificationsCount + newPromotionArticlesCount;

   const activateSwitchCompany = (e: React.MouseEvent) => {
      loginState.transitionTo("COMPANY_SELECTION");
      e.preventDefault();
   };

   const showSwitchCompany = authStore.companies && authStore.companies.length > 1 && !authStore.isPunchoutSession();

   const logoutAndRedirect: MouseEventHandler = () => {
      if (authStore.isPunchoutSession()) {
         loginState.transitionTo("NOT_LOGGED_IN");
         window.location.replace("/");
      }
      redirectTo("/");
      loginState.transitionTo("NOT_LOGGED_IN");
   };

   const handleClick = () => {
      setShow(!show);
   };

   const handleButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === "Escape") {
         setShow(false);
      }
   };

   const listItemClasses = "px-5 py-3 hover:bg-surface-default-hover w-full text-start flex items-center";

   return (
      <Popover open={show} onOpenChange={setShow}>
         {featuresStore.notificationsEnabled && totalNotificationsCount > 0 && (
            <div className="z-10 -mr-4">
               <NotificationsBadge count={totalNotificationsCount} />
            </div>
         )}
         <PopoverTrigger className="flex" asChild>
            <Button
               ref={buttonRef}
               onKeyDown={handleButtonKeyDown}
               onClick={handleClick}
               aria-expanded={show}
               aria-label="Min konto"
               aria-controls="user-menu"
               size="iconOnly"
               iconPosition="left"
               icon={<IconUser />}
               variant="tertiary"
               data-testid="user-menu-button"
            >
               {useMediaQuery(mediaQueries.xl) && (
                  <div className="flex flex-row items-center">
                     <span className="text-nowrap">Min konto</span>
                     <span className="-mb-[2px] pl-1 text-xs">({authStore.currentCompany})</span>
                     <IconExpandMore className="w-6 h-6 -my-2 -me-2" />
                  </div>
               )}
            </Button>
         </PopoverTrigger>

         <PopoverContent>
            <ul
               className="px-0 mb-0 text-sm"
               onClick={() => {
                  buttonRef.current?.focus();
                  setShow(false);
               }}
               onKeyDown={(e) => {
                  if (e.key === "Escape") {
                     buttonRef.current?.focus();
                     setShow(false);
                  }
               }}
            >
               {showSwitchCompany && (
                  <li>
                     <button className="flex w-full" type="button" onClick={activateSwitchCompany}>
                        <div className={listItemClasses}>Bytt firma</div>
                     </button>
                  </li>
               )}

               <div className="border-t border-b border-ink-border-subtle">
                  {menuItems.map((menuItem) => (
                     <li key={menuItem.name}>
                        <Link
                           to={menuItem.link}
                           className="hover:no-underline"
                           onClick={() =>
                              sendInternalLinkClick(
                                 menuItem.link,
                                 "User menu",
                                 getNotificationsTrackingText(menuItem.name, notificationsCount, newPromotionArticlesCount)
                              )
                           }
                        >
                           <div className={cn(listItemClasses, { "bg-surface-default-hover": menuItem.isActive })}>
                              <span>{menuItem.name}</span>
                              <span className="ml-1">
                                 {menuItem.name === "Varslinger" && <NotificationsBadge count={notificationsCount} />}
                              </span>
                              <span className="ml-1">
                                 {menuItem.name === "Kampanjer" && <NotificationsBadge count={newPromotionArticlesCount} />}
                              </span>
                           </div>
                        </Link>
                     </li>
                  ))}
               </div>

               <li>
                  <button className="flex w-full" type="button" onClick={logoutAndRedirect}>
                     <div className={listItemClasses}>
                        <IconLogout className="ml-[-2.4px] h-[24px] w-[24px]" />
                        Logg ut
                     </div>
                  </button>
               </li>
            </ul>
         </PopoverContent>
      </Popover>
   );
};

export default observer(UserMenu);
